import React, { useState } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  TextField, 
  Button,
  Slider,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import PageBackground from '../components/PageBackground';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RotateLeftOutlinedIcon from '@mui/icons-material/RotateLeftOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  Legend, 
  Tooltip as RechartsTooltip 
} from 'recharts';
import { ANIMATION_PRESETS } from '../theme/animations';

// Interface for criteria
interface Criterion {
  name: string;
  key: keyof CriteriaValues;
  description: string;
  tooltip: string;
}

// Interface for criteria values
interface CriteriaValues {
  marketVolatility: number;
  compQuality: number;
  propertyUniqueness: number;
  appraiserExperience: number;
  appraisalThoroughness: number;
  valueJustification: number;
}

// Interface for weights
interface Weights extends CriteriaValues {}

// Criteria definitions with explanations
const appraisalCriteria: Criterion[] = [
  { 
    name: 'Market Volatility', 
    key: 'marketVolatility', 
    description: 'Measures the stability of the local real estate market over the past 12 months',
    tooltip: 'Higher scores indicate more volatile markets with rapid price changes'
  },
  { 
    name: 'Comp Quality', 
    key: 'compQuality', 
    description: 'Assesses the relevance and similarity of comparable properties used',
    tooltip: 'Higher scores indicate better quality comps with closer proximity and similarity'
  },
  { 
    name: 'Property Uniqueness', 
    key: 'propertyUniqueness', 
    description: 'Evaluates how unique the property is compared to others in the market',
    tooltip: 'Higher scores indicate more unique properties that may be harder to accurately appraise'
  },
  { 
    name: 'Appraiser Experience', 
    key: 'appraiserExperience', 
    description: 'Considers the appraiser\'s experience in the specific market and property type',
    tooltip: 'Higher scores indicate more experienced appraisers with relevant local market knowledge'
  },
  { 
    name: 'Appraisal Thoroughness', 
    key: 'appraisalThoroughness', 
    description: 'Evaluates the detail and comprehensiveness of the appraisal report',
    tooltip: 'Higher scores indicate more thorough reports with detailed analysis and documentation'
  },
  { 
    name: 'Value Justification', 
    key: 'valueJustification', 
    description: 'Assesses how well the value conclusion is supported by the data provided',
    tooltip: 'Higher scores indicate stronger evidence and reasoning for the concluded value'
  },
];

// Chart colors
const CHART_COLORS = [
  '#0088FE', 
  '#00C49F', 
  '#FFBB28', 
  '#FF8042', 
  '#8884d8', 
  '#82ca9d'
];

const AppraisalCriticality: React.FC = () => {
  const theme = useTheme();
  
  // Initial state for the criteria
  const [criteriaValues, setCriteriaValues] = useState<CriteriaValues>({
    marketVolatility: 3,
    compQuality: 4,
    propertyUniqueness: 2,
    appraiserExperience: 4,
    appraisalThoroughness: 3,
    valueJustification: 3,
  });
  
  const [riskScore, setRiskScore] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  
  // Convert criteria values to format needed for pie chart
  const getChartData = () => {
    return appraisalCriteria.map((criterion, index) => ({
      name: criterion.name,
      value: criteriaValues[criterion.key],
      fill: CHART_COLORS[index % CHART_COLORS.length],
    }));
  };
  
  // Calculate risk score based on criteria values
  const calculateRiskScore = () => {
    const weights: Weights = {
      marketVolatility: 1.5,
      compQuality: 2,
      propertyUniqueness: 1,
      appraiserExperience: 1.5,
      appraisalThoroughness: 2,
      valueJustification: 2,
    };
    
    let weightedSum = 0;
    let totalWeight = 0;
    
    (Object.keys(criteriaValues) as Array<keyof CriteriaValues>).forEach(key => {
      weightedSum += criteriaValues[key] * weights[key];
      totalWeight += weights[key];
    });
    
    // Normalize to a 0-100 scale
    const normalized = (weightedSum / totalWeight) * 20;
    setRiskScore(Math.round(normalized));
  };
  
  const handleSliderChange = (key: keyof CriteriaValues) => (_: Event, newValue: number | number[]) => {
    setCriteriaValues({
      ...criteriaValues,
      [key]: newValue as number,
    });
    setRiskScore(null); // Reset risk score when sliders change
  };
  
  const handleResetClick = () => {
    setCriteriaValues({
      marketVolatility: 3,
      compQuality: 4,
      propertyUniqueness: 2,
      appraiserExperience: 4,
      appraisalThoroughness: 3,
      valueJustification: 3,
    });
    setNotes('');
    setRiskScore(null);
  };
  
  const getRiskCategory = () => {
    if (riskScore === null) return '';
    if (riskScore >= 80) return 'Low Risk';
    if (riskScore >= 60) return 'Moderate Risk';
    if (riskScore >= 40) return 'Elevated Risk';
    if (riskScore >= 20) return 'High Risk';
    return 'Critical Risk';
  };
  
  const getRiskColor = () => {
    if (riskScore === null) return theme.palette.grey[500];
    if (riskScore >= 80) return theme.palette.success.main;
    if (riskScore >= 60) return theme.palette.success.light;
    if (riskScore >= 40) return theme.palette.warning.main;
    if (riskScore >= 20) return theme.palette.warning.dark;
    return theme.palette.error.main;
  };

  return (
    <PageBackground>
      <Typography variant="h4" gutterBottom sx={{ 
        color: theme.palette.text.primary,
        mb: 3,
        ...ANIMATION_PRESETS.fadeIn
      }}>
        Appraisal Criticality Assessment
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left column with sliders */}
        <Grid item xs={12} md={6} sx={ANIMATION_PRESETS.slideInRight}>
          <Card sx={{ 
            height: '100%', 
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: theme.shadows[2],
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Evaluation Criteria
              </Typography>
              <Box sx={{ mt: 2 }}>
                {appraisalCriteria.map((criterion) => (
                  <Box key={criterion.key} sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormLabel id={`${criterion.key}-label`} sx={{ flex: 1 }}>
                          {criterion.name}
                        </FormLabel>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          {criteriaValues[criterion.key]}
                        </Typography>
                        <Tooltip title={criterion.tooltip} arrow>
                          <IconButton size="small">
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Slider
                        aria-labelledby={`${criterion.key}-label`}
                        value={criteriaValues[criterion.key]}
                        onChange={handleSliderChange(criterion.key)}
                        step={1}
                        marks
                        min={1}
                        max={5}
                        sx={{
                          color: theme.palette.primary.main,
                          '& .MuiSlider-thumb': {
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            }
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {criterion.description}
                      </Typography>
                    </FormControl>
                  </Box>
                ))}
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<RotateLeftOutlinedIcon />}
                    onClick={handleResetClick}
                    sx={ANIMATION_PRESETS.buttonHover}
                  >
                    Reset
                  </Button>
                  <Button 
                    variant="contained" 
                    startIcon={<AssessmentOutlinedIcon />}
                    onClick={calculateRiskScore}
                    sx={ANIMATION_PRESETS.buttonHover}
                  >
                    Calculate Risk
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right column with chart and results */}
        <Grid item xs={12} md={6} sx={ANIMATION_PRESETS.slideInLeft}>
          <Grid container spacing={3} direction="column">
            <Grid item>
              <Card sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: theme.shadows[2],
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Criticality Visualization
                  </Typography>
                  <Box sx={{ height: 350, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getChartData()}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          dataKey="value"
                          labelLine={false}
                          label={({ name, value }: { name: string; value: number }) => `${name}: ${value}`}
                        >
                          {getChartData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Legend />
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item>
              <Card sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: theme.shadows[2],
              }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Risk Assessment
                  </Typography>
                  
                  {riskScore !== null && (
                    <Box sx={{ mt: 2, ...ANIMATION_PRESETS.fadeIn }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 2 
                      }}>
                        <Box 
                          sx={{ 
                            width: 100, 
                            height: 100, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            backgroundColor: getRiskColor(),
                            color: '#fff',
                            boxShadow: `0 0 15px ${getRiskColor()}`,
                            transition: 'all 0.5s',
                            ...ANIMATION_PRESETS.pulse
                          }}
                        >
                          <Typography variant="h3">{riskScore}</Typography>
                        </Box>
                      </Box>
                      
                      <Typography 
                        variant="h5" 
                        align="center" 
                        gutterBottom 
                        sx={{ 
                          color: getRiskColor(),
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      >
                        {getRiskCategory()}
                      </Typography>
                      
                      <Alert 
                        severity={riskScore >= 60 ? "success" : riskScore >= 40 ? "warning" : "error"}
                        sx={{ mb: 2 }}
                      >
                        {riskScore >= 80 && "This appraisal shows strong quality indicators with minimal risk factors."}
                        {riskScore >= 60 && riskScore < 80 && "This appraisal is generally acceptable with some minor concerns."}
                        {riskScore >= 40 && riskScore < 60 && "This appraisal has several concerning factors that require additional review."}
                        {riskScore >= 20 && riskScore < 40 && "This appraisal has significant issues that need to be addressed before proceeding."}
                        {riskScore < 20 && "This appraisal has critical deficiencies that make it unreliable for lending decisions."}
                      </Alert>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <TextField
                    label="Assessment Notes"
                    multiline
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter your notes about this appraisal assessment..."
                    sx={{ mt: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      startIcon={<SaveOutlinedIcon />}
                      sx={ANIMATION_PRESETS.buttonHover}
                    >
                      Save Assessment
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </PageBackground>
  );
};

export default AppraisalCriticality; 